import { Component, Input, model, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import { BreakCasing, Casing, Fishing } from 'src/app/interfaces/casing-liner';
import { CementingIcp } from 'src/app/interfaces/cementing';
import {
  AcidificationStimulation,
  FractureStimulation,
} from 'src/app/interfaces/fracture';
import { Handover } from 'src/app/interfaces/handover';
import { PerforationIcp } from 'src/app/interfaces/perforation';
import { HandoverService } from 'src/app/services/handover.service';
import { WellService } from 'src/app/services/well.service';

@Component({
  selector: 'app-handover-detail-schematic',
  templateUrl: './handover-detail-schematic.component.html',
  styleUrls: ['./handover-detail-schematic.component.scss'],
  standalone: false,
})
export class HandoverDetailSchematicComponent implements OnInit {
  @Input() handover: Handover | undefined;

  casings: Casing[] = [];
  cementings: CementingIcp[] = [];
  perforations: PerforationIcp[] = [];
  stimulations: FractureStimulation[] = [];
  acidifications: AcidificationStimulation[] = [];
  breakCasings: BreakCasing[] = [];
  fishings: Fishing[] = [];

  readonly MIN_DEPTH = 0;
  maxDepth = 0;
  DIAGRAM_HEIGHT_PX = 0;
  readonly DEPTH_INCREMENT = 100;
  depthScale: number[] = [];
  isLoading = false;

  visibilityState = {
    cement: true,
    punzado: true,
    repunzado: true,
    fracture: true,
    stimulation: true,
    fishing: true,
    breakCasing: true,
  };

  constructor(
    private handoverService: HandoverService,
    private wellService: WellService
  ) {}

  ngOnInit(): void {
    if (this.handover?.handoverId) {
      this.isLoading = true;
      forkJoin({
        casings: this.wellService.getCasings(this.handover.wellName),
        cementings: this.handoverService.getCementingsIcp(
          this.handover?.handoverId,
          this.handover.wellName,
          this.handover?.handoverStatusId
        ),
        perforations: this.handoverService.getPerforationsIcp(
          this.handover.handoverId,
          this.handover.wellUwi,
          this.handover.handoverStatusId
        ),
        stimulations: this.handoverService.getHandoverFractureStimulations(
          this.handover.handoverId,
          this.handover.wellName,
          this.handover.handoverStatusId
        ),
        acidifications:
          this.handoverService.getHandoverAcidificationStimulations(
            this.handover.handoverId,
            this.handover.wellName,
            this.handover.handoverStatusId
          ),
        breakCasings: this.handoverService.getBreakCasings(
          this.handover.handoverId,
          this.handover.wellName,
          this.handover.handoverStatusId
        ),
        fishings: this.handoverService.getFishings(
          this.handover.handoverId,
          this.handover.wellName,
          this.handover.handoverStatusId
        ),
      }).subscribe({
        next: (result) => {
          this.casings = result.casings;
          if (this.casings.length > 0) {
            this.casings = this.markShoeOnDeepestCasingByType(this.casings);

            this.cementings = this.associateCasingToCementings(
              this.casings,
              result.cementings
            );
            this.perforations = result.perforations;
            this.stimulations = result.stimulations;
            this.acidifications = result.acidifications;
            this.breakCasings = result.breakCasings;
            this.fishings = result.fishings;

            this.generateDepthScale();
          }
        },
        error: (err) => {
          console.log(`Error: ${err}`);
        },
        complete: () => {
          this.isLoading = false;
        },
      });
    }
  }

  private markShoeOnDeepestCasingByType(casings: Casing[]): Casing[] {
    const deepestOfType: Record<string, Casing> = {};

    for (const casing of casings) {
      const type = casing.type;
      if (!deepestOfType[type] || casing.base > deepestOfType[type].base) {
        deepestOfType[type] = casing;
      }
    }

    return casings.map((casing) => ({
      ...casing,
      hasShoe: casing.casingId === deepestOfType[casing.type].casingId,
    }));
  }

  private associateCasingToCementings(
    casings: Casing[],
    cementings: CementingIcp[]
  ): CementingIcp[] {
    return cementings.map((cementing) => {
      const matchingCasing = casings.find(
        (casing) => casing.base >= cementing.base
      );

      return {
        ...cementing,
        casingType: matchingCasing ? matchingCasing.type : 'N/A',
      };
    });
  }

  private getMaxDepth(): number {
    if (this.casings.length === 0) {
      return 0;
    }
    return Math.max(...this.casings.map((casing) => casing.base));
  }

  private generateDepthScale(): void {
    this.maxDepth = this.getMaxDepth();
    this.DIAGRAM_HEIGHT_PX = this.maxDepth + 200 - 80; // Altura en píxeles de la zona de (80 = dibujo 40 de superficie - 40 de borde)
    for (
      let depth = this.MIN_DEPTH;
      depth <= this.maxDepth + 100;
      depth += this.DEPTH_INCREMENT
    ) {
      this.depthScale.push(depth);
    }
  }

  public depthToPx(depth: number): string {
    // 40px es la altura de la superficie
    const topPx =
      40 +
      ((depth - this.MIN_DEPTH) / (this.maxDepth - this.MIN_DEPTH)) *
        this.DIAGRAM_HEIGHT_PX;
    return `${topPx}px`;
  }

  public calculateHeight(from: number, to: number): string {
    const fromPx = parseFloat(this.depthToPx(from));
    const toPx = parseFloat(this.depthToPx(to));
    return `${toPx - fromPx}px`;
  }

  getCasingTooltip(casing: Casing): string {
    return `CASING\nTope: ${casing.top}m\nBase: ${
      casing.base
    }m\nTipo: ${casing.type.toUpperCase()}\nDiámetro: ${this.convertToNominalPipeSize(
      casing.diameter
    )}\nPeso: ${casing.weight}\nGrado tipo: ${casing.gradeType}`;
  }

  getCementingTooltip(CementingIcp: CementingIcp): string {
    return `CEMENTO\nTope: ${CementingIcp.top}m\nBase: ${CementingIcp.base}m\nResultado: ${CementingIcp.result}`;
  }

  getPerforationTooltip(perforation: PerforationIcp): string {
    return `${
      perforation.rePerforation == 'N' ? 'PUNZADO' : 'REPUNZADO'
    }\nTope: ${perforation.top}m\nBase: ${perforation.base}m`;
  }

  getFractureTooltip(fracture: FractureStimulation) {
    return `ESTIMULACIÓN FRACTURA\nTope: ${fracture.top}m\nBase: ${fracture.base}m`;
  }

  getStimulationTooltip(acidification: AcidificationStimulation): string {
    return `ESTIMULACIÓN ÁCIDA\nTope: ${acidification.top}m\nBase: ${acidification.base}m\n`;
  }

  getBreakCasingTooltip(breakCasing: BreakCasing): string {
    return `CASING ROTO\nTope: ${breakCasing.top}m\nBase: ${breakCasing.base}m\nTipo: ${breakCasing.type}`;
  }

  getFishingTooltip(fishing: Fishing): string {
    return `PESCA\nProfundidad: ${fishing.base}m\nEquipo: ${fishing.equipment}`;
  }

  mcd(a: number, b: number): number {
    return b ? this.mcd(b, a % b) : a;
  }

  convertToNominalPipeSize(num: number): string {
    const entero = Math.floor(num);
    const decimal = num - entero;

    if (decimal === 0) {
      return `${entero}"`;
    }

    let numerador = decimal;
    let denominador = 1;
    let factor = 1;

    while (numerador % 1 !== 0) {
      numerador = parseFloat((numerador * 10).toFixed(10));
      denominador *= 10;
      factor *= 10;

      if (denominador > 1000000) {
        break;
      }
    }

    numerador = Math.round(numerador);

    const divisorComun = this.mcd(numerador, denominador);

    const numSimplificado = numerador / divisorComun;
    const denSimplificado = denominador / divisorComun;

    return `${entero} ${numSimplificado}/${denSimplificado}"`;
  }
}
